import {html, LitElement, nothing, TemplateResult} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {SbkModal} from '../modal/modal';
import {style} from './repeatable-container.style';
import {devLog} from '@/configuration/helper';
import {SbkInput} from "@/components/input/input.ts";
import {SbkDatepicker} from "@/components/datepicker/datepicker.ts";
import {SbkCheckbox} from "@/components/checkbox/checkbox.ts";
import {SbkRadio} from "@/components/radio/radio.ts";
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin.ts";
import {SbkRadioGroup} from "@/components/radio-group/radio-group";
import {SbkCheckboxGroup} from "@/components/checkbox-group/checkbox-group.ts";
import {SbkTextarea} from "@/components/textarea/textarea.ts";
import {SbkSelect} from "@/components/select/select.ts";

// eslint-disable-next-line
declare const TYPO3: any;

@customElement('sbk-repeatable-container')
export class SbkRepeatableContainer extends FormControlDependencyMixin(LitElement) {
    static styles = style;
    @state() initialContent: Element[] | null = null;
    @property({type: String, attribute: 'data-fieldname'}) datafieldname?: string;
    @property({type: String, attribute: 'data-form-element-for-amount-of-copies'}) formElementForAmountOfCopies = '';
    @property({type: String, attribute: 'add-button-text'}) addButtonText = 'Eintrag hinzufügen';
    @property({type: String, attribute: 'container-label'}) containerLabel = 'Container';
    @property({type: String, attribute: 'delete-label'}) deleteLabel = 'entfernen';
    @property({type: String, attribute: 'modal-heading'}) modalHeading = 'Verwalten Sie Ihre Formulareinträge';
    @property({type: String, attribute: 'modal-icon'}) modalIcon = 'warning';
    @property({type: String, attribute: 'modal-title'}) modalTitle = 'löschen';
    @property({
        type: String,
        attribute: 'modal-content'
    }) modalContent = 'Möchten Sie Ihre Eingabe wirklich löschen? Die Aktion kann nicht rückgängig gemacht werden!';
    @property({
        type: String,
        attribute: 'modal-content-strong'
    }) modalContentStrong = 'Die Aktion kann nicht rückgängig gemacht werden';
    @property({type: String, attribute: 'modal-cancel-text'}) modalCancelText = 'Abbrechen';
    @property({type: String, attribute: 'modal-confirm-text'}) modalConfirmText = 'löschen';
    @property({type: Number}) min = 1;
    @property({type: Number}) max: number | undefined;
    index = 0;
    @state()
    hideAddButton = false;
    @state()
    siblings: SbkRepeatableContainer[] = [];

    constructor() {
        super();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            const children = this._getChildren();
            children.forEach(child => {
                child.setValue()
            });
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            const children = this._getChildren();
            children.forEach(child => {
                child.resetValue()
            });
        });
        this.addEventListener('form-control-dependency-required', () => {
            const children = this._getChildren();
            children.forEach(child => {
                child._required()
            });
        });
        this.addEventListener('form-control-dependency-optional', () => {
            const children = this._getChildren();
            children.forEach(child => {
                child.optional()
            });
        });
    }

    connectedCallback() {
        super.connectedCallback();
        this.siblings = Array.from(this.closest('form')?.querySelectorAll(`[id*="${this.datafieldname}."]`) ?? []);
        const idPattern = new RegExp(`(.*)(${this.datafieldname}).(.*)`, 'g');
        this.index = parseInt(this.id.replace(idPattern, `$3`));
        const form = this.closest('form');
        if (form) {
            form.addEventListener('repeatable-container-added', this._updateSiblings);
            form.addEventListener('repeatable-container-removed', this._updateSiblings);
        }
        if (this.formElementForAmountOfCopies) {
            const formElement = this.closest('form')?.querySelector(`[id$="${this.formElementForAmountOfCopies}"]`);
            if (formElement) {
                formElement.addEventListener('select-value-changed', this._addContainer);
                formElement.addEventListener('radio-group-change', this._addContainer);
            }
        }
    }

    protected firstUpdated() {
        if (this.formElementForAmountOfCopies) {
            const formElement = this.closest('form')?.querySelector(`[id$="${this.formElementForAmountOfCopies}"]`);
            if (formElement) {
                const amount = this._getAmount(formElement);
                this._handleFormElementDependency(Number(amount))
            }
        }
    }


    _updateSiblings = () => {
        this.siblings = Array.from(this.closest('form')?.querySelectorAll(`[id*="${this.datafieldname}."]`) ?? []);
    }

    render() {
        return html`
            <div class="repeatable-container">
                <fieldset class="form-field" id=${this.index}>
                    <div class="header">
                        <legend>${this.renderLabel()}</legend>
                        ${this._renderRemoveButton()}
                    </div>
                    <slot></slot>
                </fieldset>
                ${this._renderAddButton()}
            </div>
        `;
    }

    renderLabel() {
        if (this.formElementForAmountOfCopies) {
            return html`${this.containerLabel} ${this.getPosition() + 1}`;
        }
        return html`${this.containerLabel} (${this.getTYPO3Label('record')}&nbsp;${this.getPosition() + 1})`;
    }

    getTYPO3Label(key: string): string {
        return typeof TYPO3 !== "undefined" ? TYPO3.lang[key] : 'invalid';
    }

    _getChildren(): NodeListOf<SbkInput | SbkCheckbox | SbkCheckboxGroup | SbkRadioGroup | SbkTextarea | SbkSelect | SbkDatepicker> {
        return this.querySelectorAll('sbk-input, :not(sbk-checkbox-group) > sbk-checkbox, sbk-checkbox-group, sbk-radio-group, sbk-textarea, sbk-select, sbk-datepicker');
    }

    getPosition() {
        return this.siblings.indexOf(this);
    }

    removeField() {
        const event = new CustomEvent('repeatable-container-remove',
            {
                detail: {id: this.id},
                bubbles: true
            }
        );
        this.dispatchEvent(event);
    }

    private _handleFormElementDependency(amount: number) {
        this.siblings = Array.from(this.closest('form')?.querySelectorAll(`[id*="${this.datafieldname}."]`) ?? []);
        if (this.index === 0 && (isNaN(amount) || amount < 1)) {
            this.hide();
            const children = this._getChildren();
            children.forEach(child => {
                child.resetValue()
            });
        } else if (amount > 0 && this.index === 0) {
            this.show();
            const children = this._getChildren();
            children.forEach(child => {
                child.setValue()
            });
            if (this.siblings.length < amount && this.getPosition() + 1 === this.siblings.length) {
                this.addField();
            }
        } else if (amount > 1 && this.getPosition() + 1 < amount && this.siblings.length < amount && this.getPosition() + 1 === this.siblings.length) {
            this.addField();
        } else if ((isNaN(amount) || amount < 1) || (this.index !== 0 && this.getPosition() + 1 > amount)) {
            this.removeField();
        }
    }

    private _getAmount(formElement: Element) {
        if (formElement.tagName === 'SBK-SELECT') {
            return (<SbkSelect>formElement).selectedOption?.value;
        } else if (formElement.tagName === 'SBK-RADIO') {
            const element = <SbkRadio> formElement;
            const checkedRadio = <SbkRadio> document.querySelector(`sbk-radio[name='${element.name}'][checked]`)
            return checkedRadio ? checkedRadio.value : 0
        } else if (formElement.tagName === 'SBK-RADIO-GROUP') {
            const element = <SbkRadioGroup> formElement;
            const checkedRadio = <SbkRadio> element.querySelector(`sbk-radio[checked]`)
            return checkedRadio ? checkedRadio.value : 0
        }
        return 0;
    }

    private _addContainer = (e: Event) => {
        const event = <CustomEvent>e;
        const element = <Element>event.target;
        const amount = this._getAmount(element);
        this._handleFormElementDependency(Number(amount))
    }

    addField() {
        const newElement = this._prepareClonedContent();
        this.insertAdjacentElement('afterend', newElement);
        this.hideAddButton = true;
        const event = new CustomEvent('repeatable-container-added',
            {
                bubbles: true
            }
        );
        this.dispatchEvent(event);
    }

    hide(): void {
        this.hidden = true;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        return
    }

    setValue(): void {
        return
    }

    _replace(src: string, replacement: string) {
        return src.replace("%s", replacement);
    }

    private _renderAddButton() {
        if (this.formElementForAmountOfCopies || this.getPosition() + 1 < this.siblings.length || this.max && this.siblings.length >= this.max) {
            return '';
        }
        return html`
            <sbk-button
                    class="button-add"
                    icon="plus"
                    variant="secondary"
                    @click=${this.addField}>
                ${this.addButtonText}
            </sbk-button>
        `;
    }

    private _renderRemoveButton() {
        return !this.formElementForAmountOfCopies && this.siblings.length > this.min && this.index ? this._renderModal() : nothing;
    }

    private _renderModal(): TemplateResult {
        return html`
            <sbk-modal id="modal-${this.id}"
                       heading=${this._replace(this.modalTitle, this.containerLabel + ' ' + (this.getPosition() + 1))}
                       icon=${this.modalIcon}>
                <div slot="triggerElement">
                    <sbk-link
                            icon="trash"
                            href="test"
                            icon-position="left"
                            size="xs"
                            is-bold>
                        ${this.deleteLabel}
                    </sbk-link>
                </div>
                <div>
                    <p>${this._replace(this.modalContent, this.containerLabel + ' ' + (this.getPosition() + 1))}</p>
                    <p><strong>${this.modalContentStrong}</strong></p>
                </div>
                <sbk-button slot="footer" @click=${(e: Event) => {
                    this._closeModal(e);
                    this.removeField();
                }}>
                    ${this._replace(this.modalConfirmText, this.containerLabel + ' ' + (this.getPosition() + 1))}
                </sbk-button>
                <sbk-button slot="footer" variant="secondary" @click=${(e: Event) => this._closeModal(e)}>
                    ${this.modalCancelText}
                </sbk-button>
            </sbk-modal>
        `;
    }

    private _closeModal(e: Event) {
        const target = <HTMLElement>e.target;
        const modal: SbkModal | null = target.closest('sbk-modal')
        if (modal) {
            modal._closeModal(e)
        }
    }

    private _prepareClonedContent(): Element {
        const clonedElement: SbkRepeatableContainer = <SbkRepeatableContainer>this.cloneNode(true)
        const baseId = clonedElement.getAttribute('id') || '';
        clonedElement.id = this._generateUpdatedId(baseId);
        const elements1: SbkInput | SbkDatepicker | SbkSelect[] = Array.from(clonedElement.querySelectorAll('sbk-input, sbk-datepicker, sbk-textarea, sbk-select, input[type="hidden"]'))
        elements1.forEach((element: SbkInput | SbkDatepicker | SbkSelect ) => {
            element.removeAttribute('value')
            if (element.hasAttribute('datepicker-value')) {
                element.setAttribute('datepicker-value', '')
            }
            if (element instanceof SbkSelect) {
                element.options.forEach(option => {
                    option.selected = false;
                });
                element.selectedOption = element.options[0];
            }
            element.invalid = false;
            const idPattern = new RegExp(`(.*)-(${this.datafieldname})(_${this.index})?`, 'g');
            element.id = element.id.replace(idPattern, `$1-$2_${this.index + 1}`)
            const datasetPattern = new RegExp(`(.*)-(${this.datafieldname})(_${this.index})?`, 'g');
            Object.keys(element.dataset).forEach((key: string) => {
                element.dataset[key] = element.dataset[key]?.replace(datasetPattern, `$1-$2_${this.index + 1}`)
            })
            const namePattern = new RegExp(`(.*)(\\[${this.datafieldname}\\])(\\[${this.index}\\])?`, 'g');
            element.setAttribute('name', element.name.replace(namePattern, `$1$2[${this.index + 1}]`));

            if (element.fieldDependency) {
                const fieldDependencyPattern = new RegExp(`(.*)(\\[${this.datafieldname}\\])(\\[${this.index}\\])?`, 'g');
                element.setAttribute('field-dependency', element.fieldDependency.replace(fieldDependencyPattern, `$1$2[${this.index + 1}]`));
            }
        })
        const elements2: SbkCheckbox | SbkRadio | SbkRadioGroup[] = Array.from(clonedElement.querySelectorAll('sbk-checkbox, sbk-radio, sbk-radio-group, sbk-checkbox-group'))
        elements2.forEach((element: SbkCheckbox | SbkRadio | SbkRadioGroup) => {
            element.removeAttribute('checked')
            element.invalid = false;
            const idPattern = new RegExp(`(.*)-(${this.datafieldname})(_${this.index})?`, 'g');
            element.id = element.id.replace(idPattern, `$1-$2_${this.index + 1}`)
            const namePattern = new RegExp(`(.*)(\\[${this.datafieldname}\\])(\\[${this.index}\\])?`, 'g');
            element.setAttribute('name', element.name.replace(namePattern, `$1$2[${this.index + 1}]`));

            if (element.fieldDependency) {
                const fieldDependencyPattern = new RegExp(`(.*)(\\[${this.datafieldname}\\])(\\[${this.index}\\])?`, 'g');
                element.setAttribute('field-dependency', element.fieldDependency.replace(fieldDependencyPattern, `$1$2[${this.index + 1}]`));
            }
        })
        return clonedElement;
    }

    private _generateUpdatedId(baseId: string): string {
        const datafieldnamePattern = new RegExp(`(${this.datafieldname})(.${this.index})?`);

        if (datafieldnamePattern.test(baseId)) {
            return baseId.replace(datafieldnamePattern, `$1.${this.index + 1}`);
        } else {
            devLog('Das baseId Format ist ungültig oder das datafieldname ist nicht enthalten:', baseId, this.datafieldname);
            return baseId;
        }
    }

}
